import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NewsCard from "@/components/feature/NewsCard";
import StateBlock from "@/components/feature/StateBlock";
import { CardSkeleton } from "@/components/feature/NewsSkeleton";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useMockLoading } from "@/hooks/useMockLoading";
import { getCategoryByKey, getNewsByCategory } from "@/lib/news";

const timeRanges = [
  { key: "today", label: "今日" },
  { key: "week", label: "近一周" },
  { key: "month", label: "近一月" },
];

const sortOptions = [
  { key: "latest", label: "最新发布", icon: "ri-time-line" },
  { key: "hot", label: "最热阅读", icon: "ri-fire-line" },
];

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { categoryName } = useParams();
  const category = categoryName ? getCategoryByKey(categoryName) : undefined;

  useDocumentMeta({
    title: category ? `${category.name}新闻_91爆料_最新热点报道` : "分类新闻_91爆料",
    description: category
      ? `91爆料${category.name}频道，${category.description}每日更新最新${category.name}热点与独家爆料。`
      : "91爆料新闻分类频道，提供最新热点新闻与独家爆料报道。",
    keywords: category ? `91爆料,${category.name}新闻,${category.name}热点,最新新闻` : "91爆料,分类新闻",
  });

  const [subCategory, setSubCategory] = useState("全部");
  const [timeRange, setTimeRange] = useState("month");
  const [sortBy, setSortBy] = useState("latest");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { loading, reload } = useMockLoading(500);

  useEffect(() => {
    setSubCategory("全部");
    setPage(1);
    setTimeRange("month");
    setSortBy("latest");
  }, [categoryName]);

  useEffect(() => {
    setPage(1);
  }, [subCategory, timeRange, sortBy]);

  const filtered = useMemo(() => {
    if (!category) return [];
    let list = getNewsByCategory(category.name);
    if (subCategory !== "全部") {
      list = list.filter((n) => n.subCategory === subCategory);
    }
    if (timeRange === "today") list = list.filter((n) => n.publishDate.startsWith("2026-10-16"));
    else if (timeRange === "week") list = list.filter((n) => n.publishDate >= "2026-10-10");
    if (sortBy === "hot") {
      list = [...list].sort((a, b) => b.views - a.views);
    } else {
      list = [...list].sort((a, b) => (a.publishDate < b.publishDate ? 1 : -1));
    }
    return list;
  }, [category, subCategory, timeRange, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageNews = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!category) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-16 md:px-6">
        <StateBlock
          icon="ri-file-unknow-line"
          title="未找到该分类"
          description="你访问的分类不存在或已被调整，可以返回首页浏览全部新闻。"
          actionLabel="返回首页"
          onAction={() => {
            window.REACT_APP_NAVIGATE?.("/");
          }}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6">
      {/* 面包屑 */}
      <nav className="flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="font-medium text-foreground-800">{category.name}</span>
      </nav>

      {/* 分类头部 */}
      <section className="mt-4 rounded-lg border border-background-200 bg-background-50 p-6">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-article-line text-xl"></i>
          </span>
          <div>
            <h1 className="font-heading text-2xl font-black text-foreground-900 md:text-3xl">{category.name}新闻</h1>
            <p className="mt-1 text-sm text-foreground-500">{category.description}</p>
          </div>
        </div>
      </section>

      {/* 子分类标签 */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        {category.subCategories.map((sub) => (
          <button
            key={sub}
            type="button"
            onClick={() => setSubCategory(sub)}
            className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              subCategory === sub
                ? "bg-primary-500 text-background-50"
                : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
            }`}
          >
            {sub}
          </button>
        ))}
      </div>

      {/* 筛选栏 */}
      <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4">
        <div className="flex items-center justify-between md:hidden">
          <span className="text-sm font-medium text-foreground-800">筛选条件</span>
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm text-primary-600"
          >
            {filtersOpen ? "收起" : "展开"}
            <i className={`ri-arrow-down-s-line transition-transform ${filtersOpen ? "rotate-180" : ""}`}></i>
          </button>
        </div>

        <div className={`${filtersOpen ? "mt-4 flex" : "hidden"} flex-col gap-4 md:flex md:flex-row md:items-center md:justify-between`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-500">时间范围：</span>
            {timeRanges.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTimeRange(t.key)}
                className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                  timeRange === t.key
                    ? "bg-accent-500 font-medium text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-500">排序：</span>
            {sortOptions.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSortBy(s.key)}
                className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                  sortBy === s.key
                    ? "bg-primary-500 font-medium text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                <i className={s.icon}></i>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 结果统计 */}
      <div className="mt-5 flex items-center justify-between">
        <p className="text-sm text-foreground-500">
          共找到 <span className="font-bold text-primary-600">{filtered.length}</span> 条相关新闻
        </p>
      </div>

      {/* 新闻列表 */}
      <div className="mt-4">
        {loading ? (
          <CardSkeleton count={6} />
        ) : pageNews.length === 0 ? (
          <StateBlock
            icon="ri-search-eye-line"
            title="暂无匹配的新闻"
            description="当前筛选条件下没有找到相关新闻，试试调整时间范围或子分类。"
            actionLabel="重置筛选条件"
            onAction={() => {
              setSubCategory("全部");
              setTimeRange("month");
              setSortBy("latest");
            }}
          />
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {pageNews.map((news) => (
              <NewsCard key={news.id} news={news} />
            ))}
          </div>
        )}
      </div>

      {/* 分页 */}
      {!loading && filtered.length > 0 && (
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="flex h-9 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i className="ri-arrow-left-s-line"></i>
            上一页
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setPage(i + 1)}
              className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-sm font-medium transition-colors ${
                page === i + 1
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            type="button"
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="flex h-9 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            下一页
            <i className="ri-arrow-right-s-line"></i>
          </button>
          <button
            type="button"
            onClick={reload}
            className="ml-2 flex h-9 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-secondary-100 px-3 text-sm text-secondary-800 transition-colors hover:bg-secondary-200"
          >
            <i className="ri-refresh-line"></i>
            刷新列表
          </button>
        </div>
      )}
    </div>
  );
}